import React from "react";
import styled from "@emotion/styled";
import Tier2 from "./components/PieCharts/Tier2";
import Tier1 from "./components/PieCharts/Tier1";
import Manual from "./components/PieCharts/Manual";
import TeamStats from "./components/TeamStats";
import MainChart from "./components/MainChart/MainChart";
import ActionType from "./components/PieCharts/ActionType";
import { useSelector } from "react-redux";
import { selectLeadGenerationData } from "../../../../slices/teamMonthlyStatsSlice";
import { selectTeamLeadGenerationStats } from "../../../../slices/teamStatsSlice";

const LeadGenerationOverview = () => {

  const leadGenerationData = useSelector(selectLeadGenerationData)
  const teamLeadGenerationStats = useSelector(selectTeamLeadGenerationStats)

  return (
    <LeadGenerationPage>
      <LeadGenerationWorkspace>
        <WorkspaceHeader>
          <div>
            <WorkspaceEyebrow>Portfolio Analytics</WorkspaceEyebrow>
            <WorkspaceTitle>Lead Generation Overview</WorkspaceTitle>
          </div>
          <WorkspaceSummary>
            Review lead discovery volume, trend movement, action outcomes, and tier distribution across completed sweeps.
          </WorkspaceSummary>
        </WorkspaceHeader>

        <WorkspaceBody>
          <DistributionRail>
            <RailHeader>
              <RailTitle>Distributions</RailTitle>
              <RailSummary>Closed and dismissed investigation mix by source and tier.</RailSummary>
            </RailHeader>
            <DistributionStack>
              <ActionType />
              <Tier1 />
              <Tier2 />
              <Manual />
            </DistributionStack>
          </DistributionRail>

          <AnalysisColumn>
            <MainChart leadGenerationData={leadGenerationData} />
            <TeamStats teamLeadGenerationStats={teamLeadGenerationStats} />
          </AnalysisColumn>
        </WorkspaceBody>
      </LeadGenerationWorkspace>
    </LeadGenerationPage>
  )
}

const LeadGenerationPage = styled.main(({ theme }) => `
  width: 100%;
  height: 100%;
  min-height: 0;
  display: grid;
  padding: 0.75rem;
  overflow: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const LeadGenerationWorkspace = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.9rem;
  background: ${theme.palette.common.cardBackground};
  box-shadow: inset 0 0.1rem 0 rgba(255, 255, 255, 0.72);
`)

const WorkspaceHeader = styled.header(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.3rem;
  padding: 0.85rem 1.15rem 0.75rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

const WorkspaceEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.15rem;
  color: ${theme.palette.primary.main};
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  text-transform: uppercase;
`)

const WorkspaceTitle = styled.h1(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.42rem;
  font-weight: 800;
  line-height: 1.15;
`)

const WorkspaceSummary = styled.p(({ theme }) => `
  max-width: 62rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.86rem;
  font-weight: 550;
  line-height: 1.32;
`)

const WorkspaceBody = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(14.75rem, 17rem) minmax(0, 1fr);
  gap: 0.75rem;
  padding: 0.75rem;
  overflow-x: hidden;
  overflow-y: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const DistributionRail = styled.aside(({ theme }) => `
  min-width: 0;
  min-height: 0;
  align-self: start;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const RailHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  gap: 0.12rem;
  padding: 0.55rem 0.7rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const RailTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.88rem;
  font-weight: 800;
  line-height: 1.2;
`)

const RailSummary = styled.p(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.7rem;
  font-weight: 650;
  line-height: 1.25;
`)

const DistributionStack = styled.div(`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: repeat(4, minmax(0, 1fr));
  gap: 0.45rem;
  padding: 0.5rem;
  overflow-x: hidden;
  overflow-y: hidden;
`)

const AnalysisColumn = styled.section(`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(22rem, 1fr) auto;
  gap: 0.75rem;
`)

export default LeadGenerationOverview;
